/**
 * The quick mission log, loaded for the workload section.
 *
 * Same shape as `./use-dashboard`: reads go through the store
 * (`src/db/quick-task-store.ts`), nothing here talks to Dexie directly, and the
 * list re-reads itself off the global reactive store. That last part is what
 * makes the graph live — a journey logged in this window, in another tab on the
 * same phone, or on another manager's device entirely all arrive as the same
 * announcement, and the bars move without anybody reloading a page.
 */
import { useCallback, useEffect, useState } from 'react';
import { listQuickDriverTasks, logQuickDriverTask } from '../../db/quick-task-store';
import { useStoreEvents } from '../../hooks/use-store-events';
import type { QuickDriverTask, QuickDriverTaskDraft } from '../../types/quick-task';
import type { User } from '../../types/user';

export interface QuickTasksData {
  quickTasks: QuickDriverTask[];
  loading: boolean;
  reload: () => Promise<void>;
  /**
   * Files one journey and returns it. The list is not re-read by hand
   * afterwards — the store announces the write and the subscription below picks
   * it up, which is the same path a write from another device takes.
   */
  log: (draft: QuickDriverTaskDraft) => Promise<QuickDriverTask>;
}

export function useQuickTasks(actor: User): QuickTasksData {
  const [quickTasks, setQuickTasks] = useState<QuickDriverTask[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setQuickTasks(await listQuickDriverTasks());
  }, []);

  useEffect(() => {
    void (async () => {
      await reload();
      setLoading(false);
    })();
  }, [reload]);

  useStoreEvents(reload, ['tasks']);

  const log = useCallback(
    (draft: QuickDriverTaskDraft) => logQuickDriverTask(actor, draft),
    [actor],
  );

  return { quickTasks, loading, reload, log };
}
